import * as Sentry from '@sentry/react';

// Copied from PDFObject: https://github.com/pipwerks/PDFObject/blob/master/pdfobject.js#L101
const isPDFSupported = (): boolean => {
  try {
    const nav = window?.navigator;
    const ua = window?.navigator?.userAgent;
    const isIE = 'ActiveXObject' in window;

    /*
          There is a coincidental correlation between implementation of window.promises and native PDF support in desktop browsers
          We use this to assume if the browser supports promises it supports embedded PDFs
          Is this fragile? Sort of. But browser vendors removed mimetype detection, so we're left to improvise
      */
    const isModernBrowser = window?.Promise !== undefined;

    // Older browsers still expose the mimeType
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const supportsPdfMimeType = nav?.mimeTypes?.['application/pdf'] !== undefined;

    // Safari on iPadOS doesn't report as 'mobile' when requesting desktop site, yet still fails to embed PDFs
    const isSafariIOSDesktopMode =
      nav.platform !== undefined &&
      nav.platform === 'MacIntel' &&
      nav.maxTouchPoints !== undefined &&
      nav.maxTouchPoints > 1;

    // Quick test for mobile devices.
    const isMobileDevice = isSafariIOSDesktopMode || /Mobi|Tablet|Android|iPad|iPhone/.test(ua);

    // Firefox started shipping PDF.js in Firefox 19. If this is Firefox 19 or greater, assume PDF.js is available
    const isFirefoxWithPDFJS =
      !isMobileDevice && /irefox/.test(ua) && ua.split('rv:').length > 1
        ? parseInt(ua.split('rv:')[1].split('.')[0], 10) > 18
        : false;

    /* ----------------------------------------------------
         Supporting functions
         ---------------------------------------------------- */

    const createAXO = function (type: string) {
      let ax;
      try {
        ax = new ActiveXObject(type);
      } catch {
        ax = null; // ensure ax remains null
      }
      return ax;
    };

    // If either ActiveX support for "AcroPDF.PDF" or "PDF.PdfCtrl" are found, return true
    // Constructed as a method (not a prop) to avoid unneccesarry overhead -- will only be evaluated if needed
    const supportsPdfActiveX = function () {
      return !!(createAXO('AcroPDF.PDF') || createAXO('PDF.PdfCtrl'));
    };

    // Determines whether PDF support is available
    const result =
      // As of Sept 2020 no mobile browsers properly support PDF embeds
      !isMobileDevice &&
      // We're moving into the age of MIME-less browsers. They mostly all support PDF rendering without plugins.
      (isModernBrowser ||
        // Modern versions of Firefox come bundled with PDFJS
        isFirefoxWithPDFJS ||
        // Browsers that still support the original MIME type check
        supportsPdfMimeType ||
        // Pity the poor souls still using IE
        (isIE && supportsPdfActiveX()));

    return result;
  } catch (e) {
    Sentry.captureException(e);
    return false;
  }
};

export default isPDFSupported;
